import "../HomeTax/HomeTax.css";
import "./ConfigGrupos.css";
import { ReactNode, useEffect, useState } from "react";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { setGroupAdministrator } from "../../redux/actions/fluxoNSimilar";
import { getUser } from "../../redux/actions/user";
import HeaderProtected from "../../componentes/headerProtected/header";
import Sidebar from "../../componentes/sideBar/SideBar";
import HeadingConfigGrupos, {
  HeadingConfigGruposTab,
} from "../../componentes/ConfigGrupos/HeadingConfigGrupos";
import { SupportFooter } from "../../componentes/supportFooter/supportFooter";
import { SkeletonWrapper } from "../../componentes/SkeletonWrapper/sleketonWrapper";
import { useAdminGroups } from "../../hooks/useAdminGroups";
import RegrasGrupoForm from "../../componentes/ConfigGrupos/RegrasGrupoForm";
import ParticipantesGrupoTable from "../../componentes/ConfigGrupos/ParticipantesGrupoTable";
import ModalNovoGrupo from "../../componentes/ConfigGrupos/ModalNovoGrupo";
import Notification from "../../componentes/fluxo-tax/Notification/notification";
import { useModalState } from "../../hooks/useModalState";
import { useNotification } from "../../hooks/useNotification";
import { useRoles } from "../../hooks/roles";
import SucessIcon from "../../assets/svgs/sucess.svg";
import IconAlert from "../../assets/svgs/icon-alert.svg";
import { GroupItem } from "../../types/gruposResponsabilidade";
import Textnotifications from "../../utils/notifications";

const groupTabs: HeadingConfigGruposTab[] = [
  { id: "regras", label: "Regras do Grupo" },
  { id: "participantes", label: "Participantes" },
];

interface ConfigGruposLayoutProps {
  children: ReactNode;
}

const ConfigGruposLayout = ({ children }: ConfigGruposLayoutProps) => (
  <>
    <Sidebar />
    <HeaderProtected />
    <main className="homeMain configGroupsMain">{children}</main>
    <SupportFooter />
  </>
);

const ConfigGrupos = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { groupId } = useParams<{ groupId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    adminGroups: availableGroups,
    isUserLoaded,
    isUserError,
  } = useAdminGroups();
  const { isEngineer, isTax } = useRoles();
  const isEngineerTransfer = isEngineer && !isTax;
  const transferAdminModal = useModalState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshedGroupId, setRefreshedGroupId] = useState<string | null>(null);
  const [notificationIcon, setNotificationIcon] = useState(SucessIcon);
  const {
    notification,
    labelNotification,
    backgroundColor,
    setNotification,
    showNotification,
  } = useNotification();

  const selectedGroup = availableGroups.find(
    (group) => groupId !== undefined && String(group.id) === groupId,
  );
  const isResolvingRequestedGroup =
    !!groupId && !selectedGroup && refreshedGroupId !== groupId;
  const enabledTabs = groupTabs.filter((tab) => !tab.hidden && !tab.disabled);
  const requestedTab = searchParams.get("tab");
  const activeTab =
    requestedTab && enabledTabs.some((tab) => tab.id === requestedTab)
    ? requestedTab
    : enabledTabs[0]?.id;
  const backToGroupsList =
    searchParams.get("from") === "grupos-responsabilidade";

  useEffect(() => {
    if (!selectedGroup || !activeTab || requestedTab === activeTab) return;

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("tab", activeTab);
    setSearchParams(nextSearchParams, { replace: true });
  }, [activeTab, selectedGroup, requestedTab, searchParams, setSearchParams]);

  useEffect(() => {
    if (!groupId || selectedGroup || !isUserLoaded || refreshedGroupId === groupId) {
      return;
    }

    setRefreshedGroupId(groupId);
    dispatch(getUser());
  }, [dispatch, groupId, isUserLoaded, refreshedGroupId, selectedGroup]);

  const handleGroupChange = (nextGroupId: string) => {
    if (nextGroupId === groupId) return;
    navigate(
      {
        pathname: `/config-grupos/${nextGroupId}`,
        search: searchParams.toString(),
      },
      { replace: true },
    );
  };

  const handleTabChange = (tabId: string) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("tab", tabId);
    setSearchParams(nextSearchParams, { replace: true });
  };

  const resolveNextAdminGroup = (updatedGroups: GroupItem[], currentGroupId: string) =>
    isEngineerTransfer
      ? updatedGroups.find((group) => group.id !== currentGroupId) ?? updatedGroups[0]
      : updatedGroups.find((group) => group.id === currentGroupId) ?? updatedGroups[0];

  const handleParticipantPromotedToAdmin = async (): Promise<boolean> => {
    if (!selectedGroup) return false;

    setNotificationIcon(SucessIcon);
    showNotification("Participante definido como administrador com sucesso.");

    try {
      const updatedUser = await dispatch(getUser()).unwrap();
      const updatedUserAdminGroups = updatedUser?.admin_groups;
      const updatedGroups: GroupItem[] = Array.isArray(updatedUserAdminGroups)
        ? updatedUserAdminGroups
        : [];

      if (isEngineerTransfer && updatedGroups.length === 0) {
        navigate("/home-engenheiro", {
          replace: true,
          state: {
            successMessage: "Você não é mais administrador deste grupo.",
          },
        });
        return false;
      }

      const nextGroup = resolveNextAdminGroup(updatedGroups, selectedGroup.id);

      if (nextGroup && nextGroup.id !== selectedGroup.id) {
        navigate(
          {
            pathname: `/config-grupos/${nextGroup.id}`,
            search: searchParams.toString(),
          },
          { replace: true },
        );
        return false;
      }

      // Continua no mesmo grupo (Tax/Admin ou nenhuma troca necessária): seguro recarregar os participantes.
      return true;
    } catch (error) {
      setNotificationIcon(IconAlert);
      showNotification(
        "Participante definido como administrador, mas não foi possível atualizar os grupos. Atualize a página para ver os dados mais recentes.",
        "#FFF4CE",
      );
      console.error("Erro ao atualizar grupos após definir novo administrador:", error);
      return false;
    }
  };

  const handleTransferAdmin = async (data: {
    engineerResponsibleId: string;
  }) => {
    if (!selectedGroup) return;

    setIsSubmitting(true);

    const successMessage = "Administração transferida com sucesso.";

    try {
      await dispatch(
        setGroupAdministrator({
          groupId: selectedGroup.id,
          newAdministratorId: data.engineerResponsibleId,
        }),
      ).unwrap();
    } catch (error: any) {
      setNotificationIcon(IconAlert);
      showNotification(
        Textnotifications.getSetGroupAdministratorError(error?.message),
        "#FFD9D9",
      );
      console.error("Erro ao transferir administração do grupo:", error);
      setIsSubmitting(false);
      return;
    }

    transferAdminModal.close();

    try {
      const updatedUser = await dispatch(getUser()).unwrap();
      const updatedUserAdminGroups = updatedUser?.admin_groups;
      const updatedGroups: GroupItem[] = Array.isArray(updatedUserAdminGroups)
        ? updatedUserAdminGroups
        : [];

      setNotificationIcon(SucessIcon);
        showNotification(successMessage);

      if (isEngineerTransfer && updatedGroups.length === 0) {
        navigate("/home-engenheiro", {
          replace: true,
          state: {
            successMessage:
              "Administração transferida com sucesso! Você não é mais um administrador.",
          },
        });
        return;
      }

      const nextGroup = resolveNextAdminGroup(updatedGroups, selectedGroup.id);

      if (nextGroup) {
        navigate(
          {
            pathname: `/config-grupos/${nextGroup.id}`,
            search: searchParams.toString(),
          },
          { replace: true },
        );
      }
    } catch (error) {
      setNotificationIcon(IconAlert);
      showNotification(
        "Administração transferida com sucesso, mas não foi possível atualizar os grupos. Atualize a página para ver os dados mais recentes.",
        "#FFF4CE",
      );
      console.error("Erro ao atualizar grupos após transferir administração:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isUserError) {
    return (
      <ConfigGruposLayout>
        <ConfigGruposLoadError />
      </ConfigGruposLayout>
    );
  }

  if (!isUserLoaded || isResolvingRequestedGroup) {
    return (
      <ConfigGruposLayout>
        <section
          className="configGroupsLoading"
          aria-busy="true"
          aria-live="polite"
          aria-label="Carregando grupos"
        >
          <SkeletonWrapper loading={true} />
        </section>
      </ConfigGruposLayout>
    );
  }

  if (!selectedGroup) {
    const firstGroup = availableGroups[0];
    return firstGroup ? (
      <Navigate
        to={{
          pathname: `/config-grupos/${firstGroup.id}`,
          search: searchParams.toString(),
        }}
        replace
      />
    ) : (
      <ConfigGruposLayout>
        <ConfigGruposNotFound />
      </ConfigGruposLayout>
    );
  }

  return (
    <ConfigGruposLayout>
      <HeadingConfigGrupos
        title="Configuração do Grupo"
        groupOptions={availableGroups}
        selectedGroupId={groupId}
        selectedGroup={selectedGroup}
        onGroupChange={handleGroupChange}
        action={{
          label: "Transferir Administração",
          variant: "danger",
          onClick: transferAdminModal.open,
        }}
        tabs={groupTabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      {activeTab === "participantes" ? (
        <section
          className="containerRequest"
          role="tabpanel"
          aria-label="Participantes"
        >
          <ParticipantesGrupoTable
            key={groupId}
            groupId={selectedGroup.id}
            groupName={selectedGroup.name}
            onAdminTransferred={handleParticipantPromotedToAdmin}
          />
        </section>
      ) : (
        <section
          className="configGroupsTabContent"
          role="tabpanel"
          aria-label="Regras do Grupo"
        >
          <RegrasGrupoForm
            key={groupId}
            backToGroupsList={backToGroupsList}
          />
        </section>
      )}

      {transferAdminModal.isOpen && (
        <ModalNovoGrupo
          mode="edit-admin"
          groupName={selectedGroup.name}
          currentAdmin={selectedGroup.engineerResponsible}
          editAdminContent={{
            title: "Transferir Administração",
            ...(isEngineerTransfer
              ? {
                  currentAdminLabel: "VOCÊ É O ADMINISTRADOR ATUAL",
                  transferWarning:
                    "Ao confirmar, você deixará de ser o Administrador de Engenharia deste grupo e passará a atuar como participante.",
                }
              : {}),
          }}
          isSubmitting={isSubmitting}
          onClose={transferAdminModal.close}
          onConfirm={handleTransferAdmin}
        />
      )}

      {notification && (
        <Notification
          label={labelNotification}
          backgroundError={backgroundColor}
          setNotification={setNotification}
          img={notificationIcon}
        />
      )}
    </ConfigGruposLayout>
  );
};

const ConfigGruposNotFound = () => {
  const navigate = useNavigate();

  return (
    <section className="configGroupsNotFound" role="alert">
      <h1>Grupo não encontrado</h1>
      <p>O grupo informado não está disponível para configuração.</p>
      <button
        type="button"
        className="configGroupsBackButton"
        onClick={() => navigate("/grupos-responsabilidade")}
      >
        Voltar para Grupos de Responsabilidade
      </button>
    </section>
  );
};

const ConfigGruposLoadError = () => (
  <section className="configGroupsNotFound" role="alert">
    <h1>Não foi possível carregar os grupos</h1>
    <p>Ocorreu um erro ao carregar os grupos. Tente novamente mais tarde.</p>
  </section>
);

export default ConfigGrupos;
